import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import Router from 'next/router'
import {
  cancelDirectOfferByBuyer,
  cancelDirectOfferBySeller,
  makeOfferByBuyer,
  makeOfferBySeller,
  acceptOfferByBuyer,
  acceptOfferBySeller,
  getSectionsStateKey,
} from '../../api-client/listing'
import { ROUTES } from '../../constants'
import { Button } from '../button'
import { PriceFormField } from '../inputs'
import Modal from '../modal'
import PurchaseMade from '../purchase-made'
import ConfirmAccept from './confirm-accept'
import ConfirmCancel from './confirm-cancel'
import CounterOffersRemaining from './counter-offers-remaining'
import Offers from './offers'
import UserInfo from './user-info'
import useNegotiationInfo from './use-negotiation-info'
import OfferCancelled from './offer-cancelled'
import { parseISO } from '../../utils/date-service'

const VIEWS = {
  Offers: 1,
  ConfirmAccept: 2,
  ConfirmCancel: 3,
  OfferCancelled: 4,
  PurchaseMade: 5,
}

export default function NegotiationModal({
  forBuyer,
  vehicleId,
  userId,
  directOfferId,
  buyNowListingId,
  isDisabled,
  onClose,
}) {
  const { query } = useRouter()
  const [view, setView] = useState(VIEWS.Offers)
  const [amount, setAmount] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [error, setError] = useState(false)
  const [canGoToPurchasePage, setCanGoToPurchasePage] = useState(false)

  const {
    data: { offers, counterOffers = null } = {},
    refetch: refetchOffers,
  } = useNegotiationInfo({
    forBuyer,
    directOfferId,
    vehicleId,
    buyNowListingId,
  })

  const offeredAmountToAccept = useMemo(
    () =>
      forBuyer
        ? offers
            ?.slice(0)
            ?.sort(
              (a, b) =>
                parseISO(b.offeredAt, { withTime: true }) -
                parseISO(a.offeredAt, { withTime: true }),
            )
            ?.find((offer) => offer.sellerId)?.offeredAmount
        : offers
            ?.slice(0)
            ?.sort(
              (a, b) =>
                parseISO(b.offeredAt, { withTime: true }) -
                parseISO(a.offeredAt, { withTime: true }),
            )
            ?.find((offer) => offer.buyerId)?.offeredAmount,
    [forBuyer, offers],
  )

  const { mutate: mutateCancelForSeller, isLoading: mutatingCancelBySeller } = useMutation(
    {
    mutationFn: cancelDirectOfferBySeller,
      onSuccess: () => {
        refetchOffers()
        onClose()
      },
    },
  )

  const { mutate: mutateCancelForBuyer, isLoading: mutatingCancelByBuyer } = useMutation(
    {
    mutationFn: cancelDirectOfferByBuyer,
      onSuccess: () => {
        refetchOffers()
        onClose()
      },
    },
  )

  const { mutate: mutateCounterForSeller, isLoading: mutatingCounterBySeller } = useMutation(
    {
    mutationFn: makeOfferBySeller,
      onSuccess: () => setAmount(''),
      onError: () => setError(true),
    },
  )

  const { mutate: mutateCounterForBuyer, isLoading: mutatingCounterByBuyer } = useMutation(
    {
    mutationFn: makeOfferByBuyer,
      onSuccess: () => setAmount(''),
      onError: () => setError(true),
    },
  )

  const { mutate: mutateAcceptForSeller, isLoading: mutatingAcceptBySeller } = useMutation(
    {
    mutationFn: acceptOfferBySeller,
      onSuccess: () => setView(VIEWS.PurchaseMade),
    },
  )

  const { mutate: mutateAcceptForBuyer, isLoading: mutatingAcceptByBuyer } = useMutation(
    {
    mutationFn: acceptOfferByBuyer,
      onSuccess: () => setView(VIEWS.PurchaseMade),
    },
  )

  const handleCancel = () => {
    forBuyer ? mutateCancelForBuyer({ directOfferId }) : mutateCancelForSeller({ directOfferId })
  }

  const handleCounter = () => {
    if (amount) {
      forBuyer
        ? mutateCounterForBuyer({ buyNowListingId, offerAmount: +amount })
        : mutateCounterForSeller({ directOfferId, offerAmount: +amount })
    } else setError(true)
  }

  const handleAccept = () => {
    forBuyer ? mutateAcceptForBuyer({ directOfferId }) : mutateAcceptForSeller({ directOfferId })
  }

  const handlePriceChange = (event) => {
    setAmount(event.target.value)
    if (error) setError(false)
  }

  const goToPurchasePage = () => Router.push(`${ROUTES.PurchaseBuyNow}/${buyNowListingId}`)

  const signalrConRef = useRef(null)
  const queryClient = useQueryClient()

  const setUpSignalRConnection = useCallback(
    async (vehicleId) => {
      const connection = new HubConnectionBuilder()
        .withUrl(process.env.NEXT_PUBLIC_SIGNALR_LISTING_DOMAIN)
        .withAutomaticReconnect()
        .build()

      connection.on('SellerMadeOffer', () => {
        refetchOffers()
      })

      connection.on('BuyerMadeOffer', () => {
        refetchOffers()
      })

      connection.on('OfferAcceptedByBuyer', (buyerId) => {
        if (buyerId === userId) setView(VIEWS.PurchaseMade)
        else setErrorMessage('This vehicle has been sold')
      })

      connection.on('OfferAcceptedBySeller', (buyerId) => {
        if (buyerId === userId) setView(VIEWS.PurchaseMade)
        else setErrorMessage('This vehicle has been sold')
      })

      connection.on('DirectOfferCancelled', () => {
        setView(VIEWS.OfferCancelled)
      })

      connection.on('VehicleSold', () => {
        if (!forBuyer) queryClient.invalidateQueries([getSectionsStateKey, vehicleId])
        setCanGoToPurchasePage(true)
      })

      try {
        await connection.start()
      } catch (err) {
        alert("SignalR couldn't connect")
        console.log("SignalR couldn't connect: " + err)
      }

      if (connection.state === HubConnectionState.Connected) {
        connection.invoke('AddToGroup', vehicleId).catch((err) => {
          console.log("SignalR couldn't AddToGroup " + err)
        })
      }
      return connection
    },
    [refetchOffers, forBuyer, userId, queryClient],
  )

  useEffect(() => {
    if (!signalrConRef.current) {
      const id = vehicleId ? vehicleId : query.vehicleId
      setUpSignalRConnection(id).then((con) => {
        signalrConRef.current = con
      })
    }

    return () => signalrConRef.current?.stop()
  }, [setUpSignalRConnection, query.vehicleId, vehicleId])

  const isBusy =
    mutatingCancelByBuyer ||
    mutatingCancelBySeller ||
    mutatingCounterBySeller ||
    mutatingCounterByBuyer ||
    mutatingAcceptByBuyer ||
    mutatingAcceptBySeller

  return (
    <Modal
      onClose={onClose}
      closeOnClickOutside={view !== VIEWS.PurchaseMade && forBuyer}
      hideCloseBtn={view === VIEWS.PurchaseMade && forBuyer}
    >
      <div className='wrapper'>
        {view === VIEWS.Offers && (
          <>
            <div className='offersHeader'>
              <div className='flex alignCenter justifyBetween'>
                <UserInfo userId={userId} />
                <div className='headerActions'>
                  <button
                    className='flatBtn cancel'
                    onClick={() => setView(VIEWS.ConfirmCancel)}
                    disabled={isBusy || isDisabled}
                  >
                    Cancel offer
                  </button>
                </div>
              </div>
            </div>
            <Offers
              offers={offers || []}
              forBuyer={forBuyer}
              isDisabled={isDisabled || isBusy}
              onClick={() => setView(VIEWS.ConfirmAccept)}
            />

            <div className='offersFooter'>
              <div className='error'>{errorMessage}</div>
              <CounterOffersRemaining count={counterOffers} />
              <div className='offersActions flex alignCenter justifyBetween'>
                <PriceFormField
                  className='amountInput'
                  placeholder='Please enter offer (Only numbers will be accepted)'
                  value={amount}
                  error={error}
                  onChange={handlePriceChange}
                  onKeyDown={(e) => e.key === 'Enter' && handleCounter()}
                  disabled={counterOffers === 0 || isDisabled || errorMessage}
                />
                <Button
                  disabled={isBusy || counterOffers === 0 || isDisabled || errorMessage}
                  onClick={handleCounter}
                >
                  Counter
                </Button>
              </div>
            </div>
          </>
        )}

        {view === VIEWS.ConfirmAccept && (
          <ConfirmAccept
            onBack={() => setView(VIEWS.Offers)}
            onAccept={handleAccept}
            amount={offeredAmountToAccept}
          />
        )}

        {view === VIEWS.ConfirmCancel && (
          <ConfirmCancel onBack={() => setView(VIEWS.Offers)} onCancel={handleCancel} />
        )}
        {view === VIEWS.OfferCancelled && <OfferCancelled onClose={onClose} />}
        {view === VIEWS.PurchaseMade && (
          <PurchaseMade
            isDisabled={!canGoToPurchasePage}
            forBuyer={forBuyer}
            onClose={forBuyer ? goToPurchasePage : onClose}
          />
        )}
      </div>

      <style jsx>{`
        .wrapper {
          --offersHeaderHeight: 5rem;
          --offersFooterHeight: 12rem;
          --offersVerticalPadding: 1.1rem;

          position: relative;
          overflow: auto;
          max-width: 90vw;
          width: 80rem;
          height: 68.1rem;
          max-height: 90vh;
          padding: var(--offersVerticalPadding) 1.5rem;
          border-radius: 1rem;
          background: #fff;
        }

        .offersHeader {
          height: var(--offersHeaderHeight);

          button {
            color: var(--alert-color);
            font-size: 1.4rem;
          }
        }

        .offersFooter {
          height: var(--offersFooterHeight);

          .offersActions {
            margin: 1rem 0 0;
          }

          .error {
            margin: 0.5rem 0 1.5rem;
            color: var(--alert-color);
            text-align: center;
            font-size: 1.6rem;
            font-weight: 700;
          }

          :global(button) {
            width: 11.4rem;
            min-width: unset;
            padding: unset;
            margin: 0 0 0 0.35rem;
          }

          :global(.amountInput) {
            flex: 1;
          }

          :global(input) {
            text-align: center;
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            --offersFooterHeight: 11rem;

            max-width: 100%;
            width: 100%;
            height: 100%;
            max-height: 100%;
            padding: 4rem 0.9rem 0;
          }

          .offersHeader {
            button {
              font-size: 1.2rem;
            }
          }

          .offersFooter {
            :global(.inputContainer input) {
              font-size: 1.3rem;
            }

            :global(button) {
              width: 7.6rem;
              min-width: unset;
              padding: unset;
              margin: 0 0 0 0.35rem;
              font-size: 1.2rem;
            }

            .error {
              font-size: 1.2rem;
            }
          }
        }
      `}</style>
    </Modal>
  )
}
